import styled from "styled-components";
import { Breakpoints } from "../AppTheme";
import { Children } from "react";
import React from "react";
import { LocTextLineProps } from "../shared/shared.types"

/* Typography */

//identical to StyledCardTitle
export const StyledLocationTitle = styled.h2`
  margin: 0; /* kills default h2 margin (big spacing culprit) */
  font-size: 1.25em; /* tweak later if needed */
  line-height: 1.2;
  color: var(--lib-primary-gray);
  font-weight: 700;
  text-decoration: none;
  font-weight: bold;
  /* if you’ve set font variables globally, use them:
     font-family: var(--font-display);
  */
`;

//uppercase headings
export const StyledSectionHeading = styled.h2`
  font-size: 1.25em;
  color: var(--lib-primary-gray);
  text-transform: uppercase;
  font-weight: bold;
`;

export const StyledServiceNote = styled.p`
  color: var(--lib-primary-green);
  white-space: pre-wrap;
`;

/* Containers */

// Modal
export const StyledDetailsWrapper = styled.div`
  width: 100%;
  height: 100%;

  position: fixed;
  top: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 2;
  background-color: var(--lib-shadow-dark);
`;

// Organizes sections
export const StyledDetailsGrid = styled.div`
  display: flex;
  flex-direction: column;

  @media ${Breakpoints.mobile} {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    max-height: 100%;
  }
`;

// Base class for rows within StyledDetailsGrid
const StyledDetailsSection = styled.div`
  padding: var(--lib-default-spacing);
  box-sizing: border-box;
  border-bottom: 1px solid var(--lib-background);

  display: grid;

  background-color: var(--lib-foreground);

  // Pass down
  font-size: var(--lib-text-size-0);
  color: var(--lib-secondary-gray);

`;



export const StyledCardSection = styled.section`
  width: 100%;
  padding: 0;
  box-sizing: border-box;

  /* Header container */
  .details-header {
    width: 100%;
    display: block;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
    min-width: 0;
  }

  .details-header-content {
    padding-top: 12px;
  }

  /* Map banner */
  .details-map-banner {
    position: relative;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    height: 160px;
    border-radius: 18px;
    overflow: hidden;
  }

  .details-map-banner .esri-ui {
    display: none !important;
  }

  /* Map container inside banner */
  .details-map-inner {
    width: 100%;
    height: 100%;
  }

  /* Prevent map canvas from doing weird intrinsic sizing */
  .details-map-inner canvas,
  .details-map-inner .esri-view-root,
  .details-map-inner .esri-view-surface,
  .details-map-inner .esri-view-surface > canvas {
    width: 100% !important;
    height: 100% !important;
  }

  /* ===== ICON BUTTON BASE (Directions + Close) ===== */
  calcite-button.details-icon-btn {
    /* critical: remove padding so circle stays circle */
    --calcite-button-padding: 0;
    --calcite-button-padding-horizontal: 0;
    --calcite-button-padding-vertical: 0;

    /* make Calcite's internal button rounded */
    --calcite-button-corner-radius: 999px;

    display: inline-block;
  }

  /* Force the INTERNAL button part to be a perfect circle */
  calcite-button.details-icon-btn::part(button) {
    aspect-ratio: 1/1;
    padding: 0 !important;
    border-radius: 999px !important;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  /* Keep the icon visually centered */
  calcite-button.details-icon-btn::part(icon) {
    display: block;
  }

  /* ===== Close button positioning (TOP-RIGHT INSIDE MAP) ===== */
  calcite-button.details-close-btn {
    position: absolute;
    top: 12px;
    right: 3px;
    z-index: 5;

    /* Make the X bigger */
    --calcite-button-icon-size: 24px;

    --calcite-button-background-color: #ffffff;
    --calcite-button-text-color: #111111;
    --calcite-button-border-color: rgba(0,0,0,0.15);

    --calcite-button-hover-background-color: #ffffff;
    --calcite-button-press-background-color: #f2f2f2;
  }

  span.details-close-x {
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-1px); /* tiny optical centering tweak */
  }

  /* Slight “bolder” feel: tiny scale up */
  calcite-button.details-close-btn::part(icon) {
    transform: scale(1.05);
  }
    
  .details-meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .details-meta-left {
    min-width: 0; /* prevents overflow pushing button away */
    font-size: 16px;
    line-height: 1.5;
  }

  .details-kpi-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 18px;
  }

  .details-kpi {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .details-kpi-label {
    font-weight: 700;
    color: var(--lib-primary-gray);
    line-height: 1.2;
  }

  .details-kpi-value {
    margin-top: 2px;
    color: var(--lib-secondary-gray);
    line-height: 1.3;
  }

  .details-program-note {
    margin-top: 10px;
    color: var(--lib-secondary-gray);
  }

  .details-program-note a {
    color: var(--app-primary-blue);
    text-decoration: underline;
  }

  @media ${Breakpoints.mobile} {
    .details-kpi-grid {
      grid-template-columns: 1fr;
    }
}
`;

export const StyledHoursSection = styled(StyledDetailsSection)`
  height: auto;
  grid-auto-flow: row;
  grid-gap: var(--lib-default-spacing);
  overflow-y: auto;

  ${StyledServiceNote} {
    :not(#hoursDisclaimer) {
      padding-inline-start: 0.5rem;
      border-inline-start: 2px solid var(--lib-primary-green);
      margin-inline-start: var(--lib-default-spacing);
    }
  }

  > * {
    width: fit-content;
    display: grid;
    grid-auto-flow: row;
    grid-gap: var(--lib-default-spacing);

    justify-items: start;
  }
`;

export const StyledMapSection = styled(StyledDetailsSection)`
  flex-grow: 1;
  grid-template-rows: min-content 1fr; // map-section-header map

  #map-section-header {
    display: grid;
    grid-auto-flow: row;
    grid-template-columns: 1fr auto; //heading zoom-to-btn
    align-items: center;
  }

  // Hide title, map on desktop
  ${StyledSectionHeading}, > :nth-child(2) {
    display: none;
  }

  // Center on desktop
  #zoom-to-btn {
    margin-left: auto;
    margin-right: auto;
  }

  @media ${Breakpoints.mobile} {
    height: auto;
    //Add gap here as it won't respect "display: none" in desktop layout
    grid-gap: var(--lib-default-spacing);

    // Green headings and map
    ${StyledSectionHeading}, > :nth-child(2) {
      display: flex;
    }
    // Map
    > :nth-child(2) {
      min-height: 5rem;
      height: 100%;
    }
  }
`;

export const StyledMinMaxSection = styled(StyledDetailsSection)`
  height: min-content;

  ${StyledSectionHeading} {
    padding-bottom: var(--lib-default-spacing);
  }

  .minmax-row {
    padding-bottom: calc(var(--lib-default-spacing) / 2);

    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 18rem auto;
    gap: calc(var(--lib-default-spacing) * 2);
  }
`;

export const StyledFieldSection = styled(StyledDetailsSection)`
  height: min-content;
  grid-auto-flow: row;
  grid-gap: var(--lib-default-spacing);

  // only bold if no section title
  :first-child(${StyledServiceNote}) {
    font-weight: bold;
  }


    /* === Care details tile layout (mockup-style) === */
  .details-care-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px 24px; /* row / column gap */
    margin-top: 4px;
  }

  /* 2 columns on wider modal widths (adjust as needed) */
  @media (min-width: 600px) {
    .details-care-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  .details-care-item {
    display: grid;
    grid-template-columns: 18px 1fr;
    column-gap: 10px;
    align-items: start;
  }

  .details-care-text {
    min-width: 0;
  }

  .details-care-label {
    font-weight: 600;
    color: var(--lib-secondary-gray);
    line-height: 1.2;
    margin-bottom: 2px;
  }

  .details-care-value {
    color: var(--lib-primary-gray);
    line-height: 1.25;
  }

  .details-care-value a {
    color: var(--lib-link-blue);
    text-decoration: underline;
  }

  /* Program line below the grid */
  .details-program {
    margin-top: 8px;
    color: var(--lib-primary-gray);
  }

  .details-program a {
    color: var(--lib-link-blue);
    text-decoration: underline;
  }

  /* (keeping  existing rule, though it's not doing much currently) */
  :first-child(${StyledServiceNote}) {
    font-weight: bold;
  }

    /* === Contact provider card (mockup-style) === */
  .details-contact-card {
    background: #f3f4f7; /* soft gray like mock */
    border-radius: 10px;
    padding: 16px;
    display: grid;
    gap: 12px;
  }

  .details-contact-lede {
    color: var(--lib-primary-gray);
    line-height: 1.35;
    font-size: 14px;
  }

  .details-contact-tips {
    display: inline-block;
    margin-top: 6px;
    color: var(--lib-link-blue);
    text-decoration: underline;
  }

  .details-contact-links {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 22px;
    align-items: center;
  }

  .details-contact-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    color: var(--lib-link-blue);
    text-decoration: underline;
    
    line-height: 1.2;
  }

  .contact-phone {
    font-weight: 600;
  }

  .details-contact-link calcite-icon {
    transform: translateY(1px); /* tiny optical alignment */
  }

  .inspection-link a {
    color: var(--lib-link-blue);
    font-weight: 600;
  }

`;

/* Services components */

//wraps ServiceOption
export const StyledServiceDropdown = styled.button`
  border: none;
  background-color: var(--lib-foreground);
  cursor: pointer;

  & span {
    padding-inline-end: 10px;
  }
`;

export const StyledTable = styled.table`
  margin-inline-start: var(--lib-default-spacing);
  border-inline-start: 2px solid var(--lib-primary-green);

  table-layout: fixed;
  border-collapse: separate;

  overflow-y: auto;
`;

export const StyledHoursRow = styled.tr`
  vertical-align: baseline;

  //day name
  th {
    padding: 0.25rem 0.5rem;
    color: var(--lib-secondary-gray);
    text-align: start;
  }
  //up to 3 paragraphs in a td
  & td p {
    padding-inline-start: var(--lib-default-spacing);
    color: var(--lib-primary-gray);
  }
`;

//entire row is bold if weekday matches today's date
export const StyledHighlightedHoursRow = styled(StyledHoursRow)`
  * {
    font-weight: bold;
  }
`;

const StyledP = styled.p`
  font-size: inherit;
  > :not(:last-child) {
    :after {
      color: var(--lib-secondary-gray);
      display: inline-block;
      padding: 0 0.5rem 0 0.5rem;
    }
  }

  text-align: var(--rtl-text-align);
`;

export const DetailLocationTextLine = ({ children, dir }: LocTextLineProps) => {
  const childArr = children ? Children.toArray(children) : [];
  return childArr.filter((x) => x !== "").length !== 0 ? (
    <StyledP dir={dir}>{children}</StyledP>
  ) : null;
};

